import { useState, useEffect, useMemo } from 'react';
import { PAYMENT_METHODS } from "../../../../constants/financial";
import { getMethodLabel } from '../constants';

export const usePaymentForm = ({
    acquirers = [],
    suggestedValue = 0,
    onAddPayment
}) => {
    const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CASH);

    const [paymentData, setPaymentData] = useState({
        value: '',
        installments: '1',
        provider: '',
        brand: '',
        auth: '',
        netValue: 0
    });

    useEffect(() => {
        if (typeof suggestedValue === 'number') {
            setPaymentData(prev => ({ ...prev, value: suggestedValue.toFixed(2) }));
            if (suggestedValue === 0) {
                setPaymentMethod(PAYMENT_METHODS.CASH);
            }
        }
    }, [suggestedValue]);

    const availableBrands = useMemo(() => {
        if (!paymentData.provider || !acquirers.length) return [];
        const selectedAcquirer = acquirers.find(a => a.id === paymentData.provider);
        if (!selectedAcquirer || !selectedAcquirer.rates) return [];
        return Object.keys(selectedAcquirer.rates);
    }, [paymentData.provider, acquirers]);

    const estimatedFee = useMemo(() => {
        if (!paymentData.provider || !paymentData.brand || !acquirers.length) return null;
        if (paymentMethod !== PAYMENT_METHODS.CREDIT_CARD && paymentMethod !== PAYMENT_METHODS.DEBIT_CARD) return null;

        const acquirer = acquirers.find(a => a.id === paymentData.provider);
        if (!acquirer?.rates) return null;

        const brandConfig = acquirer.rates[paymentData.brand];
        if (!brandConfig) return null;

        let rate = 0;
        if (paymentMethod === PAYMENT_METHODS.DEBIT_CARD) {
            rate = parseFloat(brandConfig.debit) || 0;
        } else {
            const inst = parseInt(paymentData.installments) || 1;
            const creditRates = brandConfig.credit || [];
            const matchingRate = creditRates.find(c => parseInt(c.installments) === inst);
            rate = matchingRate ? parseFloat(matchingRate.rate) || 0 : 0;
        }
        return rate;
    }, [paymentData, acquirers, paymentMethod]);

    const netValueDisplay = useMemo(() => {
        const val = parseFloat(paymentData.value) || 0;
        if (val <= 0 || estimatedFee === null) return null;
        const discount = val * (estimatedFee / 100);
        return val - discount;
    }, [paymentData.value, estimatedFee]);

    useEffect(() => {
        if (availableBrands.length > 0 && !availableBrands.includes(paymentData.brand)) {
            setPaymentData(prev => ({ ...prev, brand: availableBrands[0] }));
        }
    }, [availableBrands, paymentData.brand]);

    const handleInputChange = (field, value) => {
        setPaymentData(prev => ({ ...prev, [field]: value }));
    };

    const handleAddPaymentClick = () => {
        const providerName = acquirers.find(a => a.id === paymentData.provider)?.name || paymentData.provider;

        onAddPayment({
            methodId: paymentMethod,
            methodLabel: getMethodLabel(paymentMethod),
            ...paymentData,
            netValue: netValueDisplay || parseFloat(paymentData.value) || 0,
            providerName
        });
        setPaymentData({
            value: '',
            installments: '1',
            provider: '',
            brand: '',
            auth: '',
            netValue: 0
        });
    };

    return {
        paymentMethod,
        setPaymentMethod,
        paymentData,
        handleInputChange,
        availableBrands,
        estimatedFee,
        netValueDisplay,
        handleAddPaymentClick
    };
};
